/**
 * Singleton holding the live DOM anchor for the image edit toolbar.
 *
 * `EditorView` renders an empty `<div>` inside its workspace panel and
 * passes `setImageEditToolbarHost` as the React `ref` callback. The
 * desktop `ImageEditToolbar` portals itself into that element, the
 * width-measuring hook uses it as a positioning anchor, and the editor's
 * click-outside / `selection:cleared` guards use it to recognize clicks
 * that originated inside the toolbar.
 *
 * Holding the element in a tiny module-level value avoids:
 *   - querying the DOM by id from three different files,
 *   - any race between `EditorView`'s mount and the toolbar's first
 *     render (the ref callback fires synchronously when the div is
 *     attached/detached).
 *
 * It is intentionally a singleton: there is exactly one `EditorView` per
 * route, mirroring the single piece of state we need to track.
 */
let host: HTMLElement | null = null;

/**
 * React `ref` callback that should be attached to the host `<div>` inside
 * `EditorView`. React calls it with the element on mount and with `null`
 * on unmount, so the value here always matches the live DOM.
 */
export const setImageEditToolbarHost = (element: HTMLElement | null): void => {
  host = element;
};

/** Returns the currently mounted host element, or `null` if none is mounted. */
export const getImageEditToolbarHost = (): HTMLElement | null => host;

/**
 * Convenience helper used by click-outside / `selection:cleared` guards
 * to recognise interactions that happened inside the toolbar (and should
 * therefore not deselect the active canvas object).
 */
export const isInsideImageEditToolbar = (target: EventTarget | Element | null): boolean => {
  if (!host || !target || !(target instanceof Element)) {
    return false;
  }
  return host.contains(target);
};
